import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function NotFound() {
  const [glitch, setGlitch] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [message, setMessage] = useState("PAGE NOT FOUND");

  useEffect(() => {
    const interval = setInterval(() => {
      setGlitch(true);

      setTimeout(() => {
        setGlitch(false);
      }, 180);
    }, 4500);

    return () => clearInterval(interval);
  }, []);

  const handleMouseMove = (e) => {
    const x = (e.clientX / window.innerWidth - 0.5) * 18;
    const y = (e.clientY / window.innerHeight - 0.5) * 18;

    setPosition({ x, y });
  };

  const handle404Click = () => {
    setGlitch(true);
    setMessage("FOUND SOMETHING ELSE");

    setTimeout(() => {
      window.location.href = "/";
    }, 450);
  };

  return (
    <main
      onMouseMove={handleMouseMove}
      className="min-h-screen bg-brand-bg text-white overflow-hidden relative flex items-center justify-center"
    >
      <div className="absolute inset-0 pointer-events-none opacity-[0.045]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      <div
        className="absolute w-[500px] h-[500px] rounded-full blur-[140px] opacity-[0.08] pointer-events-none"
        style={{
          background: "#664cdd",
          transform: `translate(${position.x * 2}px, ${position.y * 2}px)`,
        }}
      />

      <section className="relative z-10 w-full max-w-[1500px] px-6 md:px-10 lg:px-16 py-24">
        <div className="grid lg:grid-cols-[1fr_340px] gap-12 lg:gap-20 items-center">
          <div>
            <div className="mb-8 flex items-center gap-4">
              <span className="h-px w-12 bg-brand-mint" />

              <span className="text-xs tracking-[0.25em] uppercase text-white/40">
                {message}
              </span>
            </div>

            <Link
              to="/"
              onClick={(e) => {
                e.preventDefault();
                handle404Click();
              }}
              aria-label="Return to homepage"
              className="group relative block w-fit cursor-pointer select-none focus:outline-none"
              style={{
                transform: `translate(${position.x * 0.35}px, ${position.y * 0.35}px)`,
                transition: "transform 0.15s ease-out",
              }}
            >
              <div
                className={`
                  text-[clamp(8rem,25vw,25rem)]
                  leading-[0.72]
                  tracking-[-0.09em]
                  font-black
                  text-white
                  transition-all
                  duration-100
                  ${glitch ? "translate-x-2 opacity-80" : ""}
                `}
              >
                404
              </div>

              <div
                className={`
                  absolute inset-0
                  text-[clamp(8rem,25vw,25rem)]
                  leading-[0.72]
                  tracking-[-0.09em]
                  font-black
                  text-brand-mint
                  pointer-events-none
                  opacity-0
                  ${glitch ? "opacity-60 translate-x-[-5px]" : ""}
                `}
              >
                404
              </div>

              <div
                className={`
                  absolute inset-0
                  text-[clamp(8rem,25vw,25rem)]
                  leading-[0.72]
                  tracking-[-0.09em]
                  font-black
                  text-[#664cdd]
                  pointer-events-none
                  opacity-0
                  ${glitch ? "opacity-50 translate-x-[5px]" : ""}
                `}
              >
                404
              </div>

              <div className="absolute left-0 bottom-[-15px] h-px w-0 bg-brand-mint group-hover:w-full transition-all duration-700" />
            </Link>

            <div className="mt-14 max-w-xl">
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-semibold tracking-tight">
                This page didn't make the cut.
              </h1>

              <p className="mt-5 text-white/45 text-sm md:text-base leading-relaxed max-w-lg">
                Looks like you've wandered somewhere that doesn't exist.
                Don't worry. Even good websites lose things sometimes.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/"
                  className="group inline-flex items-center gap-4 bg-white text-[#191919] px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 hover:bg-brand-mint"
                >
                  <span>Take me home</span>

                  <span className="w-8 h-8 rounded-full bg-brand-mint text-[#191919] flex items-center justify-center transition-transform duration-300 group-hover:translate-x-1">
                    ↗
                  </span>
                </Link>

                <button
                  onClick={() => window.history.back()}
                  className="group inline-flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 px-6 py-3 rounded-full text-sm font-medium text-white transition-all duration-300"
                >
                  <span className="group-hover:-translate-x-1 transition-transform duration-300">
                    ←
                  </span>

                  <span>Go back</span>
                </button>
              </div>
            </div>
          </div>

          <div className="w-full max-w-[340px] lg:justify-self-end">
            <div className="border border-white/10 bg-white/[0.02]">
              <div className="border-b border-white/10 px-5 py-4 flex justify-between items-center">
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                  SYSTEM LOG
                </span>

                <span className="text-[10px] text-brand-mint">
                  ● ONLINE
                </span>
              </div>

              <div className="p-5 space-y-5 font-mono text-[11px]">
                <Log label="REQUEST" value={window.location.pathname} />
                <Log label="STATUS" value="404 / NOT FOUND" danger />
                <Log label="LOCATION" value="UNKNOWN" />
                <Log label="RECOVERY" value="AVAILABLE" />

                <div className="pt-3 border-t border-white/10">
                  <div className="flex justify-between text-white/30 mb-2">
                    <span>SEARCHING</span>
                    <span>73%</span>
                  </div>

                  <div className="h-[2px] bg-white/10 overflow-hidden">
                    <div className="h-full w-[73%] bg-brand-mint animate-pulse" />
                  </div>
                </div>

                <div className="pt-2 text-white/25 leading-relaxed">
                  &gt; The requested page has left the building.
                  <br />
                  &gt; We tried looking everywhere.
                  <br />
                  &gt; Nothing.
                  <br />
                  <br />
                  &gt; Recommendation:
                  <br />
                  &gt; Go somewhere better.
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <div className="absolute bottom-7 left-6 md:left-10 right-6 md:right-10 flex justify-between items-end text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-white/20">
        <span>Nothing to see here.</span>

        <span className="hidden sm:block">
          Dorado9 / Digital Marketing
        </span>

        <span>Try again ↗</span>
      </div>

      <div className="absolute top-1/2 right-[-20px] rotate-90 text-[9px] tracking-[0.4em] uppercase text-white/10">
        LOST IN THE SYSTEM
      </div>
    </main>
  );
}

function Log({ label, value, danger = false }) {
  return (
    <div>
      <div className="text-white/25 mb-1">{label}</div>
      <div className={danger ? "text-brand-mint" : "text-white/55"}>{value}</div>
    </div>
  );
}
